import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/logo.png";
import {
  Receipt,
  Users,
  Search,
  X,
  Plus,
  Trash2,
  Download,
  Loader2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ArrowRight,
  Eye,
  Building2,
  Hash,
  Settings,
  Save,
} from "lucide-react";

// ============ Shop details — persisted in localStorage ===================
const SHOP_KEY = "za_optical_shop_settings";
const DEFAULT_SHOP = {
  name: "ZA Optical",
  tagline: "Trusted Eye Care",
  address: "Main Market, Near Bus Stand, Delhi 110001",
  phone: "+91 98765 43210",
  email: "care@zaoptical.com",
  gstin: "07AABCU9603R1ZT",
  website: "www.zaoptical.com",
};
const loadShop = () => {
  try {
    const raw = localStorage.getItem(SHOP_KEY);
    return raw ? { ...DEFAULT_SHOP, ...JSON.parse(raw) } : DEFAULT_SHOP;
  } catch {
    return DEFAULT_SHOP;
  }
};

const TERMS = [
  "Goods once sold will not be taken back or exchanged.",
  "Powers guaranteed as per prescription only.",
  "Frame warranty: 6 months against manufacturing defect only.",
  "Please carry this invoice for any query or service.",
];

// ---------- helpers -----------------------------------------------------

const emptyItem = () => ({
  id: Date.now() + Math.random(),
  name: "",
  hsn: "",
  qty: 1,
  price: 0,
});

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// PDF-safe money formatter — Helvetica in jsPDF can't render ₹, use "Rs."
const fmtINR_PDF = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// integer → words (Indian number system, up to crores)
const toWords = (num) => {
  const n = Math.floor(Number(num || 0));
  if (n === 0) return "Zero";
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (x) => (x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? " " + a[x % 10] : ""));
  const three = (x) => {
    const h = Math.floor(x / 100);
    const r = x % 100;
    return (h ? a[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
  };
  let s = "";
  const cr = Math.floor(n / 10000000);
  const rest1 = n % 10000000;
  const lk = Math.floor(rest1 / 100000);
  const rest2 = rest1 % 100000;
  const th = Math.floor(rest2 / 1000);
  const rest3 = rest2 % 1000;
  if (cr) s += two(cr) + " Crore ";
  if (lk) s += two(lk) + " Lakh ";
  if (th) s += two(th) + " Thousand ";
  if (rest3) s += three(rest3);
  return s.trim();
};

const inr = fmtINR;

// Safe date formatter — returns "—" if the date string is empty/invalid
const safeDateLong = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t.getTime())
    ? "—"
    : t.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
};
const safeDateShort = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "—" : t.toLocaleDateString("en-IN");
};

// ============ Main =======================================================

export default function Invoice() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  const [customer, setCustomer] = useState(null);
  const [prescription, setPrescription] = useState(null); // latest record
  const [items, setItems] = useState([emptyItem()]);
  const [discount, setDiscount] = useState(0);
  const [gstEnabled, setGstEnabled] = useState(true);
  const [gstPercent, setGstPercent] = useState(12); // 6+6 CGST/SGST
  const [paid, setPaid] = useState(0);
  const [notes, setNotes] = useState("");
  const [invoiceNo, setInvoiceNo] = useState(
    `INV-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`
  );
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);

  // Editable shop details (persisted)
  const [shop, setShop] = useState(loadShop);
  const [shopModalOpen, setShopModalOpen] = useState(false);
  const [shopDraft, setShopDraft] = useState(shop);
  const openShopSettings = () => {
    setShopDraft(shop);
    setShopModalOpen(true);
  };
  const saveShopSettings = () => {
    setShop(shopDraft);
    localStorage.setItem(SHOP_KEY, JSON.stringify(shopDraft));
    setShopModalOpen(false);
  };

  // Load all customers once
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const { data } = await api.get("/api/customers");
      setCustomers(data.customers || []);
      if (customerId) {
        const found = (data.customers || []).find((c) => c.id === customerId);
        if (found) await selectCustomer(found);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCustomersLoading(false);
    }
  };

  const selectCustomer = async (c) => {
    setCustomer(c);
    // Fetch latest prescription/record
    try {
      const { data } = await api.get(`/api/customers/${c.id}`);
      const pres = data.prescriptions || [];
      setPrescription(pres[pres.length - 1] || null);
    } catch (e) {
      setPrescription(null);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const openPicker = () => {
    setPickerOpen(true);
    setPickerSearch("");
  };
  const pickCustomer = async (c) => {
    setPickerOpen(false);
    navigate(`/invoice/${c.id}`, { replace: true });
    await selectCustomer(c);
  };

  const filteredCustomers = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        c.customer_name?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.customer_id?.toLowerCase().includes(q)
    );
  }, [customers, pickerSearch]);

  // items handlers
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (id) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((it) => it.id !== id) : prev));
  const updateItem = (id, key, value) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [key]: value } : it)));

  // totals
  const subtotal = useMemo(
    () => items.reduce((s, it) => s + Number(it.price || 0) * Number(it.qty || 0), 0),
    [items]
  );
  const taxable = Math.max(0, subtotal - Number(discount || 0));
  const cgst = gstEnabled ? (taxable * Number(gstPercent || 0)) / 200 : 0;
  const sgst = cgst;
  const total = Math.round((taxable + cgst + sgst) * 100) / 100;
  const due = Math.max(0, total - Number(paid || 0));

  // ---- actions -------------------------------------------------------

  const handleDownloadPDF = () => {
    if (!customer) return alert("Please select a customer first.");
    if (!invoiceDate || isNaN(new Date(invoiceDate).getTime())) {
      return alert("Please enter a valid invoice date.");
    }
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const P = fmtINR_PDF; // PDF-safe money

    // ---- Watermark ---------------------------------------------------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(80);
    doc.setTextColor(240, 245, 255);
    doc.text(shop.name.toUpperCase(), pageW / 2, pageH / 2, { align: "center", angle: 45 });

    // ---- Top strip ---------------------------------------------------
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageW, 8, "F");

    // ---- Letterhead --------------------------------------------------
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text(shop.name, 40, 50);
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(100, 116, 139);
    doc.text(shop.tagline, 40, 66);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    doc.text(shop.address, 40, 82);
    doc.text(`${shop.phone}  |  ${shop.email}`, 40, 96);
    doc.text(`GSTIN: ${shop.gstin}`, 40, 110);

    // Right side: TAX INVOICE box
    doc.setFillColor(15, 23, 42);
    doc.roundedRect(pageW - 200, 34, 160, 40, 4, 4, "F");
    doc.setTextColor(255);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("TAX INVOICE", pageW - 120, 58, { align: "center" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Original for Recipient", pageW - 120, 71, { align: "center" });

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
    doc.text(`Invoice # : ${invoiceNo}`, pageW - 40, 92, { align: "right" });
    doc.text(
      `Date       : ${safeDateShort(invoiceDate)}`,
      pageW - 40, 106, { align: "right" }
    );

    // Separator
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.6);
    doc.line(40, 125, pageW - 40, 125);

    // ---- Bill To + Rx block ------------------------------------------
    const blockY = 145;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.text("BILL TO", 40, blockY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(customer.customer_name || "-", 40, blockY + 16);
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    let cy = blockY + 32;
    if (customer.phone) { doc.text(`Ph: ${customer.phone}`, 40, cy); cy += 12; }
    if (customer.email) { doc.text(`Email: ${customer.email}`, 40, cy); cy += 12; }
    if (customer.address) {
      const addrLines = doc.splitTextToSize(customer.address, 240);
      doc.text(addrLines, 40, cy);
      cy += addrLines.length * 11;
    }
    if (customer.customer_id) { doc.text(`Cust ID: ${customer.customer_id}`, 40, cy); }

    // Prescription snippet (right column)
    const hasRx =
      prescription &&
      (prescription.right_sph || prescription.left_sph ||
        prescription.right_cyl || prescription.left_cyl || prescription.add_power);
    if (hasRx) {
      const rxX = pageW / 2 + 10;
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(rxX, blockY - 12, pageW - rxX - 40, 82, 4, 4, "F");
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(rxX, blockY - 12, pageW - rxX - 40, 82, 4, 4, "S");

      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(9);
      doc.text("PRESCRIPTION", rxX + 12, blockY);

      const cols = ["EYE", "SPH", "CYL", "AXIS", "ADD"];
      const colX = [rxX + 12, rxX + 60, rxX + 100, rxX + 140, rxX + 180];
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      cols.forEach((c, i) => doc.text(c, colX[i], blockY + 16));
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.text("R", colX[0], blockY + 32);
      doc.setFont("helvetica", "normal");
      doc.text(String(prescription.right_sph || "-"), colX[1], blockY + 32);
      doc.text(String(prescription.right_cyl || "-"), colX[2], blockY + 32);
      doc.text(String(prescription.right_axis || "-"), colX[3], blockY + 32);
      doc.text(String(prescription.add_power || "-"), colX[4], blockY + 32);
      doc.setFont("helvetica", "bold");
      doc.text("L", colX[0], blockY + 48);
      doc.setFont("helvetica", "normal");
      doc.text(String(prescription.left_sph || "-"), colX[1], blockY + 48);
      doc.text(String(prescription.left_cyl || "-"), colX[2], blockY + 48);
      doc.text(String(prescription.left_axis || "-"), colX[3], blockY + 48);
      doc.text(String(prescription.add_power || "-"), colX[4], blockY + 48);
    }

    // ---- Items table -------------------------------------------------
    const body = items
      .filter((it) => it.name.trim())
      .map((it, i) => [
        i + 1,
        it.name,
        it.hsn || "-",
        it.qty,
        P(it.price),
        P(Number(it.qty) * Number(it.price)),
      ]);

    autoTable(doc, {
      startY: 245,
      head: [["#", "Description", "HSN", "Qty", "Rate", "Amount"]],
      body: body.length ? body : [["-", "-", "-", "-", "-", "-"]],
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 6, font: "helvetica" },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold", halign: "center" },
      columnStyles: {
        0: { cellWidth: 30, halign: "center" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 60, halign: "center" },
        3: { cellWidth: 40, halign: "center" },
        4: { cellWidth: 80, halign: "right" },
        5: { cellWidth: 90, halign: "right" },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    // ---- Totals block ------------------------------------------------
    const finalY = doc.lastAutoTable.finalY + 24;
    const boxW = 240;
    const boxX = pageW - boxW - 40;
    const rows = [
      ["Subtotal", P(subtotal), false],
      ["Discount", `- ${P(discount)}`, false],
      gstEnabled ? [`CGST @ ${Number(gstPercent) / 2}%`, P(cgst), false] : null,
      gstEnabled ? [`SGST @ ${Number(gstPercent) / 2}%`, P(sgst), false] : null,
      ["Grand Total", P(total), true],
      ["Advance Paid", P(paid), false],
      ["Balance Due", P(due), true],
    ].filter(Boolean);

    doc.setFontSize(10);
    let ty = finalY;
    rows.forEach(([label, val, bold]) => {
      if (label === "Grand Total") {
        doc.setFillColor(15, 23, 42);
        doc.rect(boxX, ty - 12, boxW, 20, "F");
        doc.setTextColor(255);
      } else {
        doc.setTextColor(bold ? 15 : 71, bold ? 23 : 85, bold ? 42 : 105);
      }
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.text(label, boxX + 10, ty);
      doc.text(val, boxX + boxW - 10, ty, { align: "right" });
      ty += label === "Grand Total" ? 22 : 16;
    });

    // Amount in words + notes (left column)
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Amount in words:", 40, finalY);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(71, 85, 105);
    doc.text(
      `${toWords(total)} Rupees Only`,
      40, finalY + 14,
      { maxWidth: boxX - 60 }
    );

    if (notes.trim()) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text("Notes:", 40, finalY + 40);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      doc.text(notes, 40, finalY + 54, { maxWidth: boxX - 60 });
    }

    // ---- Signature + Terms ------------------------------------------
    const footY = pageH - 150;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.6);
    doc.line(40, footY, pageW - 40, footY);

    // Terms — left half (handle wrapped lines with dynamic y)
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.text("Terms & Conditions:", 40, footY + 16);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    let termY = footY + 30;
    const termsMaxWidth = pageW / 2 - 60;
    TERMS.forEach((t, i) => {
      const lines = doc.splitTextToSize(`${i + 1}. ${t}`, termsMaxWidth);
      doc.text(lines, 40, termY);
      termY += lines.length * 10;
    });

    // Signature — right half
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(1);
    doc.line(pageW - 200, footY + 78, pageW - 40, footY + 78);
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.text(`For ${shop.name}`, pageW - 120, footY + 94, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorised Signatory", pageW - 120, footY + 107, { align: "center" });

    // Bottom strip
    doc.setFillColor(15, 23, 42);
    doc.rect(0, pageH - 22, pageW, 22, "F");
    doc.setTextColor(255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Thank you for choosing ${shop.name}  |  ${shop.website}  |  ${shop.phone}`,
      pageW / 2, pageH - 8, { align: "center" }
    );

    doc.save(`${invoiceNo}_${(customer.customer_name || "customer").replace(/\s+/g, "_")}.pdf`);
  };

  // =========================== UI =====================================

  return (
    <div className="space-y-6 print:space-y-0">
      {/* Action bar */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Tax Invoice</h1>
          <p className="mt-1 text-sm text-slate-500">
            Generate a professional GST invoice as a downloadable PDF.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            data-testid="shop-settings-btn"
            onClick={openShopSettings}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 flex-1 sm:flex-none"
          >
            <Settings size={16} /> Shop Settings
          </button>
          <button
            data-testid="invoice-pdf-btn"
            onClick={handleDownloadPDF}
            disabled={!customer}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 hover:bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 flex-1 sm:flex-none"
          >
            <Download size={16} /> Download PDF
          </button>
        </div>
      </div>

      {/* ------------ Editor panel (top strip, non-print) --------------- */}
      <div className="print:hidden rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 grid place-items-center">
              <Users size={18} className="text-blue-700" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold tracking-wider text-slate-500">Customer</p>
              <p className="text-sm font-bold text-slate-900">
                {customer ? customer.customer_name : "No customer selected"}
              </p>
            </div>
          </div>
          <button
            data-testid="select-customer-invoice"
            onClick={openPicker}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-950 hover:bg-slate-800 text-white px-4 py-2.5 text-sm font-semibold"
          >
            <Users size={15} /> {customer ? "Change Customer" : "Select Customer"}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Invoice #</label>
            <input
              data-testid="invoice-no-input"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm font-mono outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Invoice Date</label>
            <input
              data-testid="invoice-date-input"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-2 border-t border-slate-100">
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Discount (₹)</label>
            <input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">GST</label>
            <select
              value={gstEnabled ? "on" : "off"}
              onChange={(e) => setGstEnabled(e.target.value === "on")}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              <option value="on">Enable</option>
              <option value="off">Disable</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">GST %</label>
            <input type="number" min="0" max="28" step="0.5" value={gstPercent}
              disabled={!gstEnabled}
              onChange={(e) => setGstPercent(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Advance Paid (₹)</label>
            <input type="number" min="0" value={paid} onChange={(e) => setPaid(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Balance Due</label>
            <div className="mt-1 h-10 rounded-md border border-amber-200 bg-amber-50 px-3 text-sm font-bold text-amber-800 flex items-center">
              {inr(due)}
            </div>
          </div>
        </div>
      </div>

      {/* ================= Printable Invoice =========================== */}
      <div
        id="invoice-printable"
        className="relative rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60 overflow-hidden print:border-none print:shadow-none print:rounded-none"
      >
        {/* Watermark */}
        <div
          aria-hidden
          className="pointer-events-none select-none absolute inset-0 grid place-items-center opacity-[0.04]"
        >
          <span className="text-[10rem] font-black tracking-tighter text-slate-900 rotate-[-18deg]">
            {shop.name.toUpperCase()}
          </span>
        </div>

        {/* Top accent strip */}
        <div className="h-2 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 print:bg-slate-900" />

        {/* --------- Letterhead --------- */}
        <div className="relative px-6 sm:px-10 pt-8 pb-6 border-b border-slate-200">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Brand */}
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-2xl overflow-hidden shadow-md shadow-blue-600/25 shrink-0">
                <img src={logo} alt="ZA Optical" className="h-full w-full object-cover" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-none">
                  {shop.name}
                </h2>
                <p className="text-[11px] uppercase tracking-[0.24em] text-blue-700 font-semibold mt-1.5">
                  {shop.tagline}
                </p>
                <div className="mt-3 text-xs text-slate-600 space-y-0.5 leading-relaxed">
                  <p className="flex items-start gap-1.5"><MapPin size={11} className="mt-0.5 text-slate-400 shrink-0" /> {shop.address}</p>
                  <p className="flex items-center gap-1.5"><Phone size={11} className="text-slate-400" /> {shop.phone} · <Mail size={11} className="text-slate-400" /> {shop.email}</p>
                  <p className="flex items-center gap-1.5"><Building2 size={11} className="text-slate-400" /> GSTIN: <span className="font-mono font-semibold text-slate-800">{shop.gstin}</span></p>
                </div>
              </div>
            </div>

            {/* Invoice meta */}
            <div className="md:text-right">
              <div className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2">
                <Receipt size={16} />
                <div className="text-left">
                  <p className="text-[9px] uppercase tracking-widest text-white/70 leading-none">Tax Invoice</p>
                  <p className="text-[10px] leading-tight">Original for Recipient</p>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600 space-y-1">
                <p className="flex items-center gap-1.5 md:justify-end">
                  <Hash size={11} className="text-slate-400" />
                  <span className="text-slate-500">Invoice No :</span>
                  <span className="font-mono font-bold text-slate-900">{invoiceNo}</span>
                </p>
                <p className="flex items-center gap-1.5 md:justify-end">
                  <Calendar size={11} className="text-slate-400" />
                  <span className="text-slate-500">Date :</span>
                  <span className="font-semibold text-slate-900">
                    {safeDateLong(invoiceDate)}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* --------- Bill To + Prescription --------- */}
        <div className="relative px-6 sm:px-10 py-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-slate-200">
          <div>
            <p className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500 mb-2">Bill To</p>
            {customer ? (
              <>
                <p className="text-lg font-bold text-slate-900">{customer.customer_name}</p>
                <div className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                  {customer.phone && <p className="flex items-center gap-1.5"><Phone size={11} className="text-slate-400" /> {customer.phone}</p>}
                  {customer.email && <p className="flex items-center gap-1.5"><Mail size={11} className="text-slate-400" /> {customer.email}</p>}
                  {customer.address && <p className="flex items-start gap-1.5"><MapPin size={11} className="mt-0.5 text-slate-400 shrink-0" /> <span>{customer.address}</span></p>}
                  {customer.customer_id && <p className="text-slate-500">Customer ID: <span className="font-mono font-semibold text-slate-800">{customer.customer_id}</span></p>}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400">Select a customer above.</p>
            )}
          </div>

          {/* Prescription snippet */}
          {prescription && (prescription.right_sph || prescription.left_sph || prescription.add_power) ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Eye size={12} className="text-blue-600" />
                <p className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500">
                  Prescription
                </p>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left font-medium py-1 w-10">Eye</th>
                    <th className="text-center font-medium py-1">SPH</th>
                    <th className="text-center font-medium py-1">CYL</th>
                    <th className="text-center font-medium py-1">AXIS</th>
                    <th className="text-center font-medium py-1">ADD</th>
                  </tr>
                </thead>
                <tbody className="text-slate-900">
                  {[
                    { l: "R", tint: "bg-blue-100 text-blue-700", sph: prescription.right_sph, cyl: prescription.right_cyl, axis: prescription.right_axis },
                    { l: "L", tint: "bg-violet-100 text-violet-700", sph: prescription.left_sph, cyl: prescription.left_cyl, axis: prescription.left_axis },
                  ].map((r) => (
                    <tr key={r.l} className="border-t border-slate-200">
                      <td className="py-1.5">
                        <span className={`inline-grid place-items-center h-5 w-5 rounded text-[10px] font-bold ${r.tint}`}>{r.l}</span>
                      </td>
                      <td className="text-center font-mono">{r.sph || "-"}</td>
                      <td className="text-center font-mono">{r.cyl || "-"}</td>
                      <td className="text-center font-mono">{r.axis || "-"}</td>
                      <td className="text-center font-mono">{prescription.add_power || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="hidden md:block rounded-xl border border-dashed border-slate-200 grid place-items-center text-xs text-slate-400 p-4">
              No prescription linked
            </div>
          )}
        </div>

        {/* --------- Items --------- */}
        <div className="relative px-6 sm:px-10 py-6">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500">Items</p>
            <button
              onClick={addItem}
              className="print:hidden inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 hover:border-slate-500 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
            >
              <Plus size={13} /> Add Item
            </button>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white">
                <tr className="text-[11px] uppercase tracking-wider">
                  <th className="text-center p-3 font-semibold w-12">#</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                  <th className="text-center p-3 font-semibold w-24">HSN</th>
                  <th className="text-center p-3 font-semibold w-16">Qty</th>
                  <th className="text-right p-3 font-semibold w-28">Rate</th>
                  <th className="text-right p-3 font-semibold w-32">Amount</th>
                  <th className="p-3 w-10 print:hidden" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={it.id} className={idx % 2 ? "bg-slate-50/60" : ""}>
                    <td className="p-3 text-center text-slate-500 font-mono">{idx + 1}</td>
                    <td className="p-2">
                      <input
                        value={it.name}
                        onChange={(e) => updateItem(it.id, "name", e.target.value)}
                        placeholder="Item / service description"
                        className="w-full h-9 rounded-md border border-transparent hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-transparent px-2 text-sm outline-none print:border-none print:px-0"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        value={it.hsn}
                        onChange={(e) => updateItem(it.id, "hsn", e.target.value)}
                        placeholder="—"
                        className="w-full h-9 rounded-md border border-transparent hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-transparent px-2 text-sm text-center font-mono outline-none print:border-none print:px-0"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        value={it.qty}
                        onChange={(e) => updateItem(it.id, "qty", e.target.value)}
                        className="w-full h-9 rounded-md border border-transparent hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-transparent px-2 text-sm text-center outline-none print:border-none print:px-0"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={it.price}
                        onChange={(e) => updateItem(it.id, "price", e.target.value)}
                        className="w-full h-9 rounded-md border border-transparent hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-transparent px-2 text-sm text-right outline-none print:border-none print:px-0"
                      />
                    </td>
                    <td className="p-3 text-right font-semibold text-slate-900">
                      {inr(Number(it.qty) * Number(it.price))}
                    </td>
                    <td className="p-2 text-right print:hidden">
                      <button
                        onClick={() => removeItem(it.id)}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile items */}
          <div className="md:hidden space-y-3">
            {items.map((it, idx) => (
              <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-slate-500">#{idx + 1}</span>
                  <button
                    onClick={() => removeItem(it.id)}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <input
                  value={it.name}
                  onChange={(e) => updateItem(it.id, "name", e.target.value)}
                  placeholder="Item description"
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    value={it.hsn}
                    onChange={(e) => updateItem(it.id, "hsn", e.target.value)}
                    placeholder="HSN"
                    className="h-10 rounded-md border border-slate-200 px-3 text-sm text-center font-mono outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <input
                    type="number"
                    value={it.qty}
                    onChange={(e) => updateItem(it.id, "qty", e.target.value)}
                    placeholder="Qty"
                    className="h-10 rounded-md border border-slate-200 px-3 text-sm text-center outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <input
                    type="number"
                    value={it.price}
                    onChange={(e) => updateItem(it.id, "price", e.target.value)}
                    placeholder="Rate"
                    className="h-10 rounded-md border border-slate-200 px-3 text-sm text-right outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="text-right text-sm">
                  <span className="text-slate-500">Amount: </span>
                  <span className="font-semibold text-slate-900">
                    {inr(Number(it.qty) * Number(it.price))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --------- Totals + Notes --------- */}
        <div className="relative px-6 sm:px-10 pb-8 grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Left — amount in words + notes */}
          <div className="md:col-span-3 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500">
                Amount in Words
              </p>
              <p className="mt-1.5 text-sm text-slate-800 italic">
                {toWords(total) || "—"} Rupees Only
              </p>
            </div>
            <div className="print:hidden">
              <label className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500">
                Notes / Payment Terms
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Warranty, delivery timeline, payment mode…"
                className="mt-1.5 w-full resize-none rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            {notes.trim() && (
              <div className="hidden print:block rounded-xl border border-slate-200 p-4">
                <p className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500 mb-1">Notes</p>
                <p className="text-sm text-slate-800 whitespace-pre-line">{notes}</p>
              </div>
            )}
            {/* Terms */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-[10px] uppercase font-bold tracking-[0.18em] text-slate-500 mb-2">
                Terms & Conditions
              </p>
              <ol className="text-[11px] text-slate-600 space-y-1 list-decimal list-inside leading-snug">
                {TERMS.map((t) => (<li key={t}>{t}</li>))}
              </ol>
            </div>
          </div>

          {/* Right — totals box */}
          <div className="md:col-span-2">
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <dl className="text-sm">
                <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-slate-600">
                  <dt>Subtotal</dt>
                  <dd className="font-mono text-slate-800">{inr(subtotal)}</dd>
                </div>
                {Number(discount) > 0 && (
                  <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-slate-600">
                    <dt>Discount</dt>
                    <dd className="font-mono text-red-600">- {inr(discount)}</dd>
                  </div>
                )}
                {gstEnabled && (
                  <>
                    <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-slate-600">
                      <dt>CGST @ {Number(gstPercent) / 2}%</dt>
                      <dd className="font-mono text-slate-800">{inr(cgst)}</dd>
                    </div>
                    <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-slate-600">
                      <dt>SGST @ {Number(gstPercent) / 2}%</dt>
                      <dd className="font-mono text-slate-800">{inr(sgst)}</dd>
                    </div>
                  </>
                )}
                <div className="flex justify-between px-4 py-3 bg-slate-900 text-white">
                  <dt className="text-sm font-bold uppercase tracking-wider">Grand Total</dt>
                  <dd className="text-lg font-black font-mono">{inr(total)}</dd>
                </div>
                {Number(paid) > 0 && (
                  <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-emerald-700 bg-emerald-50/40">
                    <dt className="font-semibold">Advance Paid</dt>
                    <dd className="font-mono font-semibold">{inr(paid)}</dd>
                  </div>
                )}
                <div className="flex justify-between px-4 py-3 bg-amber-50 text-amber-900 font-bold">
                  <dt>Balance Due</dt>
                  <dd className="font-mono">{inr(due)}</dd>
                </div>
              </dl>
            </div>

            {/* Signature */}
            <div className="mt-6 text-right">
              <div className="inline-block text-center">
                <div className="h-12 border-b-2 border-slate-900 w-56" />
                <p className="mt-2 text-sm font-bold text-slate-900">For {shop.name}</p>
                <p className="text-[11px] text-slate-500">Authorised Signatory</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="bg-slate-900 text-white text-center text-[11px] tracking-wider py-2.5 relative">
          Thank you for choosing {shop.name} · {shop.website}
        </div>
      </div>

      {/* ============ Picker modal ============ */}
      {pickerOpen && (
        <div
          className="print:hidden fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">Select Customer</h3>
              <button
                onClick={() => setPickerOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-5 pt-4">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
                <Search size={16} className="text-slate-400" />
                <input
                  data-testid="invoice-picker-search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search name, phone or ID"
                  className="w-full bg-transparent py-2.5 text-sm outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto p-3 flex-1">
              {customersLoading ? (
                <div className="grid place-items-center py-10">
                  <Loader2 className="animate-spin text-blue-600" size={24} />
                </div>
              ) : filteredCustomers.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-10">No customers found.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredCustomers.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        data-testid={`inv-pick-${c.id}`}
                        onClick={() => pickCustomer(c)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-3 text-left hover:bg-slate-50 rounded-lg"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {c.customer_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {c.phone} · {c.customer_id}
                          </p>
                        </div>
                        <ArrowRight size={14} className="text-slate-400 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ Shop Settings Modal ============ */}
      {shopModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShopModalOpen(false)}
        >
          <div
            data-testid="shop-settings-modal"
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <Settings size={18} className="text-slate-700" />
                <h3 className="text-lg font-bold text-slate-900">Shop Settings</h3>
              </div>
              <button
                onClick={() => setShopModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3">
              {[
                { key: "name", label: "Shop Name" },
                { key: "tagline", label: "Tagline" },
                { key: "gstin", label: "GSTIN" },
                { key: "phone", label: "Phone" },
                { key: "email", label: "Email" },
                { key: "website", label: "Website" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                    {f.label}
                  </label>
                  <input
                    data-testid={`shop-${f.key}`}
                    value={shopDraft[f.key] || ""}
                    onChange={(e) => setShopDraft({ ...shopDraft, [f.key]: e.target.value })}
                    className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              ))}
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                  Address
                </label>
                <textarea
                  data-testid="shop-address"
                  rows={2}
                  value={shopDraft.address || ""}
                  onChange={(e) => setShopDraft({ ...shopDraft, address: e.target.value })}
                  className="mt-1 w-full resize-none rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                onClick={() => setShopModalOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                data-testid="shop-save-btn"
                onClick={saveShopSettings}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-950 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold"
              >
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print stylesheet */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
          aside, .lg\\:ml-72 > main > .print\\:hidden { display: none !important; }
          .lg\\:ml-72 { margin-left: 0 !important; padding: 0 !important; }
          #invoice-printable { border: none !important; box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
